"use client";

import { useCallback } from "react";
import Image from "next/image";
import { Icon } from "@/components/ui/icon";
import {
  useJobWizardStore,
  type WizardStageItem,
} from "@/features/jobs/store/job-store";
import {
  SortableStageList,
  splitStages,
  type StageItem,
} from "@/features/jobs/components/stage-cards";

export function StepStages() {
  const { form, updateForm } = useJobWizardStore();
  const stages = form.stages;
  // ATS-imported job: the stages already exist server-side, mirrored from the
  // remote pipeline. Structure (names come from the ATS; add/delete would
  // break the mapping) is locked via atsLinked, but color/description are
  // local-only cosmetics the sync never touches — keep those editable.
  const atsLinked = form.sourcingType === "ats_import";

  const handleAddStage = useCallback(
    (name: string, color: string, description: string) => {
      const newStage: WizardStageItem = {
        id: `custom-${Date.now()}`,
        name,
        color,
        description,
        is_system: false,
      };
      const { leading, editable, trailing } = splitStages(stages);
      updateForm("stages", [...leading, ...editable, newStage, ...trailing]);
    },
    [stages, updateForm],
  );

  const removeStage = useCallback(
    (id: string) => {
      updateForm(
        "stages",
        stages.filter((s) => s.id !== id),
      );
    },
    [stages, updateForm],
  );

  const handleUpdateStage = useCallback(
    (id: string, patch: Partial<StageItem>) => {
      updateForm(
        "stages",
        stages.map((s) => (s.id === id ? { ...s, ...patch } : s)),
      );
    },
    [stages, updateForm],
  );

  const handleReorder = useCallback(
    (reordered: StageItem[]) => {
      updateForm("stages", reordered);
    },
    [updateForm],
  );

  return (
    <div className="flex flex-col gap-3 h-full">
      {atsLinked && (
        <div className="flex items-center gap-1.5 text-contrast/60 shrink-0">
          <Icon name="IconCircleInfo" size={14} className="opacity-70" />
          <span className="text-xs leading-4">
            Stages mirror your ATS pipeline and stay in sync automatically.
            Colors and descriptions are yours to customize.
          </span>
        </div>
      )}

      <div className="relative flex-1 min-h-0 rounded-md">
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none rounded-md"
        >
          <div className="absolute bg-contrast/[0.02] inset-0 rounded-md" />
          <Image
            alt=""
            className="absolute max-w-none mix-blend-screen object-cover opacity-[0.03] rounded-md size-full"
            src="/assets/grid-bg.png"
            width={1920}
            height={1080}
          />
        </div>

        <SortableStageList
          stages={stages}
          onUpdate={handleUpdateStage}
          onRemove={removeStage}
          onAdd={handleAddStage}
          onReorder={handleReorder}
          atsLinked={atsLinked}
        />
      </div>
    </div>
  );
}
